import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useRouter } from 'next/router';
import { VscChevronUp, VscChevronDown } from 'react-icons/vsc';
import { CONTENT_GROUP, OPEN_STATES } from './constants';

const ContentGroupAccordion = ({
  name,
  groupSections,
  sectionsData,
  VariantComponent,
  openState,
  groupSectionProps,
  groupSectionTitleProps,
  groupContentWrapperProps,
  groupContentProps
}) => {
  const router = useRouter();
  const { asPath } = router;

  const sectionPairs = useMemo(
    () =>
      sectionsData.map(([sectionName, sectionLabel]) => ({
        sectionName,
        sectionLabel,
        panelId: `${sectionName}-panel`
      })),
    [sectionsData]
  );

  const sectionNames = useMemo(() => sectionPairs.map(p => p.sectionName), [sectionPairs]);

  const baseInitialOpen = useMemo(() => {
    if (openState === OPEN_STATES.ALL_OPEN) return new Set(sectionNames);
    if (openState === OPEN_STATES.ALL_CLOSED && sectionNames.length) return new Set();

    return new Set([sectionNames[0]]);
  }, [openState, sectionNames]);

  const [openSet, setOpenSet] = useState(baseInitialOpen);

  useEffect(
    () => {
      const rawHash = (asPath.split('#')[1] || '').trim();
      if (rawHash && sectionNames.includes(rawHash) && !openSet.has(rawHash)) {
        setOpenSet(prev => {
          const next = new Set(prev);
          next.add(rawHash);
          return next;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [asPath, sectionNames]
  );

  const WrapperComponent = VariantComponent || 'div';

  const baseClass = `${CONTENT_GROUP}-accordion`;
  const itemsClass = `${baseClass}__items`;
  const itemClass = `${baseClass}__item`;
  const headerBtnClass = `${baseClass}__header-btn`;
  const panelClass = `${baseClass}__panel`;
  const headingClass = `${baseClass}__heading`;
  const titleClass = `${baseClass}__title`;
  const chevronClass = `${baseClass}__chevron`;
  const chevronIconClass = `${baseClass}__chevron-icon`;

  const onToggle = sectionName => {
    setOpenSet(prev => {
      const next = new Set(prev);
      const willOpen = !next.has(sectionName);

      if (willOpen) next.add(sectionName);
      else next.delete(sectionName);

      const baseUrl = asPath.split('#')[0];
      const newUrl = willOpen ? `${baseUrl}#${sectionName}` : baseUrl;

      router.replace(`/Resolver`, newUrl, { shallow: true, scroll: false });

      return next;
    });
  };

  return (
    <div className={baseClass}>
      <div className={itemsClass} aria-label={name}>
        {groupSections.map((groupSection, index) => {
          const { sectionName, sectionLabel, panelId } = sectionPairs[index];
          const isOpen = openSet.has(sectionName);

          return (
            <div
              key={sectionName}
              className={classnames(itemClass, { [`${itemClass}--open`]: isOpen })}
              {...groupSectionProps}>
              <h3 className={headingClass}>
                <button
                  id={sectionName}
                  type="button"
                  className={classnames(headerBtnClass, { [`${headerBtnClass}--open`]: isOpen })}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => onToggle(sectionName)}>
                  <span className={titleClass} {...groupSectionTitleProps}>
                    {sectionLabel}
                  </span>

                  <span aria-hidden="true" className={chevronClass}>
                    {isOpen ? (
                      <VscChevronUp className={chevronIconClass} />
                    ) : (
                      <VscChevronDown className={chevronIconClass} />
                    )}
                  </span>
                </button>
              </h3>

              <WrapperComponent
                id={panelId}
                role="region"
                aria-labelledby={sectionName}
                className={panelClass}
                hidden={!isOpen}
                data-section={sectionName}
                {...groupContentWrapperProps}>
                <div {...groupContentProps}>{groupSection}</div>
              </WrapperComponent>
            </div>
          );
        })}
      </div>
    </div>
  );
};

ContentGroupAccordion.propTypes = {
  name: PropTypes.string.isRequired,
  groupSections: PropTypes.array.isRequired,
  sectionsData: PropTypes.array.isRequired,
  VariantComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.elementType]),
  openState: PropTypes.oneOf(Object.values(OPEN_STATES)),
  groupSectionProps: PropTypes.object,
  groupSectionTitleProps: PropTypes.object,
  groupContentWrapperProps: PropTypes.object,
  groupContentProps: PropTypes.object
};

ContentGroupAccordion.defaultProps = {
  VariantComponent: null,
  openState: OPEN_STATES.ALL_CLOSED,
  groupSectionProps: {},
  groupSectionTitleProps: {},
  groupContentWrapperProps: {},
  groupContentProps: {}
};

export default ContentGroupAccordion;
